import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiTarget, FiEye, FiShield, FiCpu, FiUsers } from "react-icons/fi";

const WhoWeAre = () => {
  const { t } = useTranslation();

  const values = [
    { icon: FiCpu, title: "Innovation", desc: "Pushing limits using state-of-the-art tech." },
    { icon: FiShield, title: "Quality", desc: "Clean code, robust testing, premium products." },
    { icon: FiUsers, title: "Customer Centric", desc: "We design with your users and growth in mind." },
  ];

  return (
    <section className="py-20 md:py-28 bg-white dark:bg-[#080808] transition-colors duration-500 overflow-hidden relative">
      {/* Decorative blurred blob */}
      <div className="absolute top-1/2 left-0 w-80 h-80 rounded-full bg-blue-500/5 dark:bg-purple-500/5 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Who We Are & Values */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 flex flex-col gap-6"
          >
            <span className="text-xs font-bold text-blue-600 dark:text-purple-400 uppercase tracking-widest">
              {t('whoWeAreLabel', 'Get to know us')}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white mt-2 tracking-tight">
              {t('whoWeAreTitle')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed">
              {t('whoWeAreDescription')}
            </p>

            {/* Core Values grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
              {values.map((val, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -5 }}
                  className="p-6 rounded-2xl bg-slate-50 dark:bg-[#111111] border border-slate-100 dark:border-neutral-800/60 shadow-sm"
                >
                  <val.icon className="w-6 h-6 text-blue-600 dark:text-purple-400 mb-3" />
                  <h4 className="font-bold text-sm text-slate-900 dark:text-white mb-1">{val.title}</h4>
                  <p className="text-xs text-slate-500 dark:text-neutral-400 leading-normal">{val.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column: Mission and Vision Cards */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
              className="p-6 sm:p-8 rounded-3xl bg-slate-50/50 dark:bg-[#111111]/30 border border-slate-200/50 dark:border-neutral-800/40 backdrop-blur-sm relative group overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 dark:bg-purple-500/5 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500" />
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 rounded-2xl">
                  <FiTarget className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                    {t('missionTitle', 'Our Mission')}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    {t('missionDescription', 'To empower companies globally by translating visionary concepts into robust, efficient, and beautifully functional software architectures.')}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ scale: 1.02 }}
              className="p-6 sm:p-8 rounded-3xl bg-slate-50/50 dark:bg-[#111111]/30 border border-slate-200/50 dark:border-neutral-800/40 backdrop-blur-sm relative group overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 dark:bg-pink-500/5 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500" />
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-50 dark:bg-purple-950/20 text-purple-600 dark:text-purple-400 rounded-2xl">
                  <FiEye className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                    {t('visionTitle', 'Our Vision')}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    {t('visionDescription', 'To become a globally preferred innovation ally, recognized for bringing absolute technical excellence, sustainable design, and seamless customer care.')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default WhoWeAre;
